import React from "react";

const ENABLED = import.meta.env.DEV;

const __w = (typeof window !== "undefined" ? (window as unknown as { __loopDetectorInstalled?: boolean }) : null);
const ALREADY_INSTALLED = !!(__w && __w.__loopDetectorInstalled);

if (ENABLED && typeof window !== "undefined" && !ALREADY_INSTALLED) {
  __w!.__loopDetectorInstalled = true;
  const origUseState = React.useState;
  const origUseReducer = React.useReducer;

  const counters = new WeakMap<Function, { count: number; firstAt: number; stack: string }>();
  const safeJson = (v: unknown): string => {
    try {
      return JSON.stringify(v)?.slice(0, 200) ?? String(v);
    } catch {
      return String(v);
    }
  };
  const REPORT_THRESHOLD = 5;
  const WINDOW_MS = 1000;

  function wrapSetter<T extends Function>(setter: T, label: string): T {
    const wrapped = ((...args: unknown[]) => {
      const now = performance.now();
      let entry = counters.get(setter);
      if (!entry || now - entry.firstAt > WINDOW_MS) {
        entry = { count: 0, firstAt: now, stack: "" };
        counters.set(setter, entry);
      }
      entry.count += 1;
      if (entry.count === REPORT_THRESHOLD) {
        entry.stack = new Error().stack ?? "";
        const callerStack = new Error().stack ?? "";
        const dump = {
          ts: new Date().toISOString(),
          label,
          arg: safeJson(args[0]),
          creationStack: entry.stack,
          callerStack,
        };
        // eslint-disable-next-line no-console
        console.error(`[loop-detector][HIT] ${label} arg=${safeJson(args[0])}`);
        // eslint-disable-next-line no-console
        console.error(`[loop-detector][CREATE] ${entry.stack.replace(/\n/g, " | ")}`);
        // eslint-disable-next-line no-console
        console.error(`[loop-detector][CALLER] ${callerStack.replace(/\n/g, " | ")}`);
        try {
          const prev = JSON.parse(localStorage.getItem("__loopDetectorDump") ?? "[]");
          prev.push(dump);
          localStorage.setItem("__loopDetectorDump", JSON.stringify(prev.slice(-20)));
        } catch {}
        (window as unknown as { __loopDetectorDump?: unknown[] }).__loopDetectorDump =
          (window as unknown as { __loopDetectorDump?: unknown[] }).__loopDetectorDump ?? [];
        ((window as unknown as { __loopDetectorDump: unknown[] }).__loopDetectorDump).push(dump);
      }
      return (setter as unknown as (...a: unknown[]) => unknown)(...args);
    }) as unknown as T;
    return wrapped;
  }

  function getCallerLabel(): string {
    const stack = new Error().stack ?? "";
    const lines = stack.split("\n");
    // Skip frames: Error, getCallerLabel, patchedUseState/Reducer, then real caller
    for (let i = 3; i < Math.min(lines.length, 12); i++) {
      const l = lines[i].trim();
      if (l && !l.includes("loop-detector")) return l.slice(0, 200);
    }
    return "unknown";
  }

  (React as unknown as { useState: typeof React.useState }).useState = function patchedUseState<S>(
    initial?: S | (() => S),
  ) {
    const label = getCallerLabel();
    const result = origUseState(initial as S);
    return [result[0], wrapSetter(result[1], `useState@${label}`)] as ReturnType<typeof React.useState>;
  } as typeof React.useState;

  (React as unknown as { useReducer: typeof React.useReducer }).useReducer = function patchedUseReducer(
    reducer: (...a: unknown[]) => unknown,
    initialArg: unknown,
    init?: (a: unknown) => unknown,
  ) {
    const label = getCallerLabel();
    const result = (origUseReducer as unknown as (...a: unknown[]) => unknown[])(reducer, initialArg, init);
    return [result[0], wrapSetter(result[1] as Function, `useReducer@${label}`)] as ReturnType<typeof React.useReducer>;
  } as typeof React.useReducer;

  // eslint-disable-next-line no-console
  console.log("[loop-detector] installed");
}

export function useRenderCounter(name: string): void {
  if (!ENABLED || typeof window === "undefined") return;
  const w = window as unknown as { __renderCounts?: Record<string, { count: number; firstAt: number }> };
  w.__renderCounts = w.__renderCounts ?? {};
  const now = performance.now();
  const entry = w.__renderCounts[name];
  if (!entry || now - entry.firstAt > 1000) {
    w.__renderCounts[name] = { count: 1, firstAt: now };
  } else {
    entry.count += 1;
    if (entry.count === 5 || (entry.count > 5 && entry.count % 5 === 0)) {
      // eslint-disable-next-line no-console
      console.error(
        `[render-counter] "${name}" rendered ${entry.count}x within ${Math.round(
          now - entry.firstAt,
        )}ms`,
      );
    }
  }
}
